import React from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  SliderThemes,
  getColumnWidth,
  ContainerPaddings,
  SliderNavigationPositions,
  SliderNavigationButtons,
  TabsVariants,
  ContainerWidths,
  TextHorizontalAlignments as TabsHorizontalAlignments,
  useThemeUtils
} from '@dcxwam/dcx-wam-ui-foundations'
import { canUseDom } from '../../../../utils/isSsr'
import Container from '../../Container/Container'
import { SlidesProps, SliderVariantDProps } from '../types'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import { NoSsr } from '../../../functional/NoSsr/NoSsr'
import { getSliderComponent } from '../common/getSliderComponent'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'

if (canUseDom) {
  import('@dcxwam/dcx-wam-ui-foundations/css')
}

const Slide: React.FC<SlidesProps> = withComponentRefresh(
  (props: SlidesProps) => {
    const { model, gtmItems } = props
    const dataAttrs = {}
    gtmItems?.forEach((gtmItem) => {
      if (gtmItem) {
        // @ts-expect-error String as object iteral
        dataAttrs[gtmItem.key] = gtmItem.value
      }
    })
    const containerConfig = {
      model: {
        ...model,
        ...dataAttrs
      }
    }

    return <Container {...containerConfig} />
  }
)

const SliderVariantD: React.FC<
  EditableComponentPropsType<SliderVariantDProps>
> = withComponentRefresh((props) => {
  const {
    accessibilityFirst,
    accessibilityLast,
    accessibilityNext,
    accessibilityPrevious,
    id,
    background,
    adaptiveHeight,
    theme,
    tabsAriaLabel,
    tabControls,
    xfPath,
    ...rest
  } = props.model
  const editableProps = useExtractEditableProps(props, rest)

  const themeUtils = useThemeUtils()

  // @ts-expect-error AEM Model is undefined
  const slides = Object.keys(props?.model?.cqItems)?.map((cqItemKey) => {
    const {
      ':items': cqItems,
      ':itemsOrder': cqItemsOrder,
      ':type': cqType,
      // @ts-expect-error AEM Model does not have the property
      gtmItems,
      // @ts-expect-error AEM Model does not have the property
      tabLabel,
      // @ts-expect-error AEM Model does not have the property
      theme: slideTheme,
      ...itemRest
      // @ts-expect-error AEM Model is undefined
    } = props.model.cqItems[cqItemKey]

    let slideDTheme = SliderThemes.Light
    switch (slideTheme) {
      case 'light':
        slideDTheme = SliderThemes.Light
        break
      case 'dark':
        slideDTheme = SliderThemes.Dark
        break
      default:
        break
    }
    const slideConfig = {
      gtmItems,
      model: {
        cqItems,
        cqItemsOrder,
        cqType,
        ...itemRest
      }
    }

    return {
      slide: <Slide {...slideConfig} />,
      slideTheme: slideDTheme,
      slideTabLabel: tabLabel
    }
  })

  let sliderDTheme = SliderThemes.Light
  switch (theme) {
    case 'light':
      sliderDTheme = SliderThemes.Light
      break
    case 'dark':
      sliderDTheme = SliderThemes.Dark
      break
    default:
      break
  }

  const configSliderD = {
    className: 'slider-d',
    navigationPosition: SliderNavigationPositions.Mid,
    navigationConfig: {
      buttons: SliderNavigationButtons.Transparent,
      width: themeUtils.getResponsiveProp([getColumnWidth(12, 12)])
    },
    background,
    lazyLoading: true,
    keyboard: true,
    centeredSlides: true,
    slideToClickedSlide: true,
    sliderPagination: false,
    autoHeight: adaptiveHeight,
    sliderWidths: ContainerWidths.Full,
    sliderTheme: sliderDTheme,
    horizontalAlignment: TabsHorizontalAlignments.Center,
    numberOfColumns: themeUtils.getResponsiveProp([getColumnWidth(12, 12)]),
    propsPerBreakpoints: themeUtils.getResponsiveProp([
      {
        slidesPerView: 1
      }
    ]),
    sliderPadding: themeUtils.getResponsiveProp([
      { l: ContainerPaddings.Size_0 }
    ]),
    ...(tabControls && {
      sliderTabs: {
        tabsVariant: TabsVariants.Scrollable,
        'aria-label': tabsAriaLabel
      }
    }),
    slides,
    accessibilityProps: {
      firstSlideMessage: accessibilityFirst,
      lastSlideMessage: accessibilityLast,
      prevSlideMessage: accessibilityPrevious,
      nextSlideMessage: accessibilityNext
    }
  }

  const componentToDisplay = getSliderComponent({
    props,
    xfPath,
    sliderConfig: configSliderD
  })

  return (
    <NoSsr>
      <EditableComponent {...editableProps}>
        {componentToDisplay}
      </EditableComponent>
    </NoSsr>
  )
})

const SliderVariantDWithErrorBoundary = withErrorBoundary(SliderVariantD, {
  componentName: 'SliderVariantD',
  fallback: <ErrorFallback componentName="SliderVariantD" />
})

export default SliderVariantDWithErrorBoundary
